import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { PageLayout } from '../../shared/PageLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { Settings, LogOut, Link as LinkIcon, Users, CheckCircle2, AlertCircle, AlertTriangle, Edit2, Key, Unlink } from 'lucide-react';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import { type LearnerAccount, type ParentAccount, type AccountsState, LINK_CODE_LENGTH, LINK_CODE_EXAMPLE } from '../../../accounts';
import { fetchLinkedPeople } from '../../../data/supabase/session';
import { AppState } from '../../../app/types';
import { useLinkChild } from '../hooks/useLinkChild';
import { usePasswordChange } from '../../shared/usePasswordChange';
import { ConsentPanel } from '../components/ConsentPanel';
import { DataRightsPanel } from '../components/DataRightsPanel';
import { unlinkParentFromLearnerOnServer } from '../../../data/supabase/parentLinks';
interface ParentPageProps {
  accounts: AccountsState;
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  viewingLearnerId: string | undefined;
  setViewingLearner: (id: string) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
}
export function SettingsPage({
  accounts,
  activeAccount,
  linkedLearners,
  linkParentToLearnerById
}: ParentPageProps) {
  const {
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updatePassword,
    unlinkParentFromLearner
  } = useAppStore();
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    clearActiveAccount
  });
  const {
    linkCode,
    setLinkCode,
    error,
    success,
    notice,
    isSubmitting,
    handleSubmit: handleLinkChild
  } = useLinkChild();
  // Hoeveel volwassenen kijken er per kind mee — van de server.
  //
  // Dit is de enige plek waar een ouder kan zien dat hij niet de enige is. Op
  // een gedeeld ouderschap is dat de kern van de transparantie-eis, en bij een
  // koppeling die niet klopt is het het enige signaal dat er iets mis is. Het
  // mag dus niet afhangen van wat er toevallig op dit apparaat staat.
  const [parentCountByLearner, setParentCountByLearner] = useState<Record<string, number>>({});
  useEffect(() => {
    void fetchLinkedPeople().then(people => {
      const next: Record<string, number> = {};
      for (const person of people) {
        if (person.relation === 'child') next[person.id] = person.parentCount;
      }
      setParentCountByLearner(next);
    });
  }, [linkedLearners.length]);

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount.displayName);
  const [editEmail, setEditEmail] = useState(activeAccount.email || '');
  // Password change — shared hook
  const pw = usePasswordChange({
    accountId: activeAccount.id,
    passwordHash: activeAccount.passwordHash,
    role: 'parent',
    updatePassword
  });
  // Unlink state
  const [unlinkLearnerId, setUnlinkLearnerId] = useState<string | null>(null);

  // Wie er verder nog meekijkt bepaalt of ontkoppelen data vernietigt. Dit
  // wordt hier afgeleid en niet in de dialoog zelf, zodat titel, tekst én
  // knoplabel gegarandeerd hetzelfde geval beschrijven.
  const unlinkLearnerName = linkedLearners.find(l => l.id === unlinkLearnerId)?.displayName ?? 'dit kind';

  // Ook dit telt op de SERVER, om dezelfde reden als hierboven — maar hier is
  // de inzet groter: dit bepaalt of de dialoog waarschuwt dat de toetsuitslagen
  // gewist worden. De lokale lijst kent een andere ouder vrijwel nooit, dus die
  // waarschuwing verscheen ook wanneer er wél iemand anders meekeek en er dus
  // niets gewist zou worden — een ouder afschrikken voor iets wat niet gebeurt.
  //
  // Zolang de telling nog niet binnen is, gaan we uit van "laatste ouder". Dat
  // is de voorzichtige kant: liever een waarschuwing te veel dan een ouder die
  // onaangekondigd de toetsuitslagen van zijn kind wist.
  const unlinkIsLastParent = unlinkLearnerId ? (parentCountByLearner[unlinkLearnerId] ?? 1) <= 1 : false;
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    setIsEditingProfile(false);
  };
  const handleConfirmUnlink = async () => {
    if (!unlinkLearnerId) return;
    const learnerId = unlinkLearnerId;
    const learner = linkedLearners.find(l => l.id === learnerId);
    const learnerName = learner?.displayName ?? 'Kind';
    setUnlinkLearnerId(null);

    // Deze knop paste eerder alléén de lokale opslag aan. De koppeling bleef
    // op de server staan, dus kwam het kind bij de volgende keer inloggen
    // gewoon terug — en behield deze ouder al die tijd toegang tot alle
    // gegevens. Erger nog: de trigger die bij het vertrek van de laatste
    // ouder de toestemming intrekt en de toetsuitslagen wist, hangt aan een
    // échte verwijdering en vuurde dus nooit.
    //
    // De server gaat daarom voor. Slaagt die niet, dan passen we lokaal ook
    // niets aan: een scherm dat zegt "ontkoppeld" terwijl de server het kind
    // nog koppelt, is een leugen die pas bij de volgende sessie uitkomt.
    const res = await unlinkParentFromLearnerOnServer(learnerId);
    if (!res.ok) {
      toast.error(res.message ?? 'Ontkoppelen is niet gelukt.');
      return;
    }
    unlinkParentFromLearner(activeAccount.id, learnerId);

    // Was dit de laatste ouder, dan heeft de database meer gedaan dan
    // meekijken stopzetten: de toestemming is ingetrokken en de bewaarde
    // toetsuitslagen zijn gewist. Dat mag niet als terloopse mededeling
    // langskomen, en "ongedaan maken" bieden zou onwaar zijn — opnieuw
    // koppelen brengt die uitslagen niet terug.
    if (res.wasLastParent) {
      toast.success(`${learnerName} is ontkoppeld. Omdat er geen ouder meer meekijkt, zijn de bewaarde toetsuitslagen verwijderd. ${learnerName} kan gewoon blijven oefenen.`, {
        duration: 10000
      });
      return;
    }
    toast.success(`${learnerName} is ontkoppeld`, {
      action: {
        label: 'Ongedaan maken',
        onClick: () => {
          linkParentToLearnerById(activeAccount.id, learnerId);
          toast.success(`${learnerName} is weer gekoppeld.`);
        }
      },
      duration: 5000
    });
  };
  return <PageLayout title="Instellingen" description="Beheer je ouderaccount en gekoppelde kinderen" data-id="element-8774" data-mp="1nt98ozn2uzbm">
      
      <div className="max-w-3xl space-y-6" data-id="element-8775" data-mp="1j06npfwd8y71">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Jouw persoonlijke gegevens" icon={Settings} accentColor="bg-emerald-100 text-emerald-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount.displayName);
        setEditEmail(activeAccount.email || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-8777" data-mp="151q4wql8bh8j">
            
                <Edit2 className="w-4 h-4 mr-2" data-id="element-8778" data-mp="v428lw7z61o9" /> Bewerk
              </Button> : null} data-id="element-8776" data-mp="1e2wq3cytpg84">
          
          <div className="space-y-6" data-id="element-8779" data-mp="2uh6do4v8r0xw">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-8780" data-mp="2uek6w7g6cpgq">
                <div data-id="element-8781" data-mp="2piv4lwcrsugq">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8782" data-mp="2picwa1cu2psg">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-8783" data-mp="23b12l4mshgkt" />
                
                </div>
                <div data-id="element-8784" data-mp="1okk15la5mqnx">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8785" data-mp="1tl7rs5uh15hm">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-8786" data-mp="1fkx050lc7mve" />
                
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-8787" data-mp="15oll42ud4ix9">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-8788" data-mp="1am3iqx2xjbpy">
                  
                    Annuleren
                  </Button>
                  <Button onClick={handleSaveProfile} variant="primary" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-8789" data-mp="2zzguv8haa41m">
                  
                    Opslaan
                  </Button>
                </div>
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-8790" data-mp="oeq0erl6afvb">
                <div data-id="element-8791" data-mp="ocda9r59gz76">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8792" data-mp="132ucvdtmtv5n">
                    Naam
                  </label>
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-8793" data-mp="ybm0qa0x3vos">
                    {activeAccount.displayName}
                  </div>
                </div>
                <div data-id="element-8794" data-mp="181xfkrm53pag">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8795" data-mp="184jksklk5spi">
                    E-mailadres
                  </label>
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-8796" data-mp="1h2lrngrz3fkp">
                    {activeAccount.email || <span className="text-slate-400 italic" data-id="element-8797" data-mp="3nvfnp9mmhvk5">
                        Niet ingesteld
                      </span>}
                  </div>
                </div>
              </div>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-emerald-100 text-emerald-600" action={!pw.isChangingPassword ? <Button onClick={() => pw.setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-8799" data-mp="30nbmvlvtkdcg">
            
                <Edit2 className="w-4 h-4 mr-2" data-id="element-8800" data-mp="18gbxtrkrhg3" /> Wijzig
              </Button> : null} data-id="element-8798" data-mp="39myrshnrc1a9">
          
          {pw.isChangingPassword ? <form onSubmit={pw.handleChangePassword} className="space-y-4" data-id="element-8801" data-mp="3sf9f335xhqn9">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8802" data-mp="3nmzqbqgjuwmk">
                <div className="sm:col-span-2" data-id="element-8803" data-mp="3nl56ygkxfckl">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8804" data-mp="3jkv2578ves7b">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.currentPassword} onChange={e => pw.setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-8805" data-mp="k098fy5urksk" />
                
                </div>
                <div data-id="element-8806" data-mp="1xwsorp9mksuf">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8807" data-mp="22t0xokx8bpt5">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.newPassword} onChange={e => pw.setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-8808" data-mp="26tkx5v59z5ig" />
                
                </div>
                <div data-id="element-8809" data-mp="2gssib4i944g4">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-8810" data-mp="2gr7ndds0yjhq">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.confirmPassword} onChange={e => pw.setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-8811" data-mp="2qk6ccwrfoc1v" />
                
                </div>
              </div>

              {pw.passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-8812" data-mp="2zhp40jzl1omq">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-8813" data-mp="14gdhnogff8co" />
                  <p data-id="element-8814" data-mp="14eak8jcy2cxx">{pw.passwordError}</p>
                </div>}

              {pw.passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-8815" data-mp="zjda193xhxjg">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-8816" data-mp="19fzu3vofkkkf" />
                  <p data-id="element-8817" data-mp="19dwvin97pj43">{pw.passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-8818" data-mp="1n5jtqsmpt1pi">
                <Button type="button" onClick={() => pw.resetPasswordForm()} variant="secondary" data-id="element-8819" data-mp="1j62n3vvjxyir">
                
                  Annuleren
                </Button>
                <Button type="submit" variant="primary" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-8820" data-mp="1x2ybcvkdjwje">
                
                  Wachtwoord opslaan
                </Button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-8821" data-mp="3txuf9pu1nvky">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Linked Children */}
        <SettingsSection title="Gekoppelde Kinderen" description="Beheer de kinderen die aan jouw account zijn gekoppeld" icon={Users} accentColor="bg-teal-100 text-teal-600" data-id="element-8822" data-mp="2ee3ks5ej1vfb">
          
          {linkedLearners.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-8823" data-mp="24mqrragzsz7p">
              {linkedLearners.map(learner => {
            // Het aantal medekijkers komt van de SERVER, niet uit de lokale
            // accountlijst. Die lijst bevat alleen wat op dít apparaat bekend
            // is — en een andere ouder heeft hier per definitie nooit
            // ingelogd. De chips met namen die hier stonden, verschenen dus
            // vrijwel nooit, precies bij de gescheiden-oudersituatie waarvoor
            // ze bedoeld waren.
            //
            // Namen tonen we niet: de RPC geeft ze niet, en ze lokaal erbij
            // verzinnen is hoe deze sectie stil kon verouderen. Het aantal is
            // bovendien wat de ouder moet weten — dát er iemand meekijkt.
            const otherParentCount = Math.max((parentCountByLearner[learner.id] ?? 0) - 1, 0);
            return <div key={learner.id} className="p-4 rounded-xl border border-slate-200 bg-white shadow-sm hover:border-teal-200 transition-colors" data-id="element-8824" data-mp="16zhpjg66lpb3">
                  
                    <div className="flex items-center justify-between" data-id="element-8825" data-mp="x34zqtdrbvlm">
                      <div className="flex items-center gap-3" data-id="element-8826" data-mp="scestbponkot">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg" data-id="element-8827" data-mp="sav7v7t30lgp">
                          {learner.displayName.charAt(0).toUpperCase()}
                        </div>
                        <div data-id="element-8828" data-mp="ecwx0cdyz75y">
                          <div className="font-bold text-slate-900" data-id="element-8829" data-mp="eb2f76xzvv63">
                            {learner.displayName}
                          </div>
                          <div className="text-xs font-medium text-slate-500" data-id="element-8830" data-mp="3vf03nvfov4ga">
                            {learner.username}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-8831" data-mp="9709c15ofy2y">
                        <div className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100" data-id="element-8832" data-mp="95p4dy0hwka9">
                          Gekoppeld
                        </div>
                        {/*
                         `title` is geen toegankelijke naam: op een telefoon
                         verschijnt er geen tooltip, en veel schermlezers
                         negeren het. Zonder `aria-label` was dit dus een
                         naamloze knop die een kind ontkoppelt. Het tikdoel
                         was bovendien ~28px; op 44px is het op een telefoon
                         niet meer per ongeluk te raken náást de bedoeling.
                         */}
                        <button onClick={() => setUnlinkLearnerId(learner.id)} className="flex items-center justify-center w-11 h-11 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors" aria-label={`${learner.displayName} ontkoppelen van je account`} title="Ontkoppelen" data-id="element-8833" data-mp="2qvw61yb789co">
                        
                          <Unlink className="w-4 h-4" data-id="element-8834" data-mp="2vnxeta95rhxy" />
                        </button>
                      </div>
                    </div>
                    {otherParentCount > 0 && <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-8835" data-mp="22y8ekz49vlxe">
                        <div className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600" data-id="element-8836" data-mp="230bdy9rberes">
                          <Users className="w-3.5 h-3.5 text-slate-400" data-id="element-8837" data-mp="2ht4u7xeevbki" />
                          {otherParentCount === 1 ? 'Nog één andere ouder of verzorger kijkt mee' : `Nog ${otherParentCount} andere ouders of verzorgers kijken mee`}
                        </div>
                      </div>}
                  </div>;
          })}
            {/*
             Wie er meekijkt is nu half beantwoord: het aantal ouders komt van
             de server, maar over een leerkracht weet dit portaal niets — er is
             geen bron die zegt of dit kind in een klas zit. Die leegte
             stilzwijgend laten leest als "er kijkt geen leerkracht mee", en
             dat is een bewering die we niet kunnen waarmaken. Elders in de app
             deden we dat wél (de Tips-pagina stelde het omgekeerde als feit).
             Hier staat daarom expliciet wat we niet weten.
             */}
            <div className="sm:col-span-2 text-xs text-slate-500 leading-relaxed px-1" data-id="element-8838" data-mp="nhde4rxhfqax">
              Gaat je kind naar school met een gekoppelde klas, dan ziet de
              leerkracht de voortgang ook. Dat loopt via de school en is hier
              nog niet zichtbaar — vraag het na bij de leerkracht.
            </div>
            </div> : <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed mb-8" data-id="element-8839" data-mp="166aad1cfwsvs">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mx-auto mb-3" data-id="element-8840" data-mp="hfcnnoqtbkxf">
                <Users className="w-6 h-6" data-id="element-8841" data-mp="cmt9apkawlrk" />
              </div>
              <p className="text-slate-500 font-medium" data-id="element-8842" data-mp="7oi3qr11gxf5">
                Er zijn nog geen kinderen gekoppeld aan dit account.
              </p>
            </div>}

          <div className="pt-6 border-t border-slate-100" data-id="element-8843" data-mp="2odlavgklsma">
            <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-8844" data-mp="10cnqfjbnkwhm">
              <LinkIcon className="w-4 h-4 text-teal-500" data-id="element-8845" data-mp="vfx9kts0xwv5" />
              Nieuw kind koppelen
            </h4>
            {/*
              De lengte stond hier als los getal "6" — pal boven de opmerking
              hieronder die uitlegt waarom dat fout gaat. Precies de valstrik
              die daar beschreven staat: de code is inmiddels 8 tekens, dus
              deze zin stuurde een ouder aan het tellen op het verkeerde getal.
             */}
            <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-8846" data-mp="3699tk5lg4yri">
              Voer de koppelcode van {LINK_CODE_LENGTH} tekens in. Je kind
              vindt die in zijn of haar instellingen; een leerkracht kan hem
              ook geven. De code bevat letters en cijfers.
            </p>

            <form onSubmit={handleLinkChild} className="flex flex-col sm:flex-row gap-3" data-id="element-8847" data-mp="1vl92ruow870j">
              
              {/*
                "6-cijferig" klopte niet: een koppelcode bevat ook letters.
                De lengte komt uit `LINK_CODE_LENGTH` en niet uit een los getal —
                een hardgecodeerde maxLength kapt bij een lengtewijziging de code
                STIL af, en de ouder krijgt "code niet gevonden" zonder reden.
               */}
              <Input variant="admin" label="Koppelcode" value={linkCode} onChange={e => setLinkCode(e.target.value.toUpperCase())} placeholder={`Bijv. ${LINK_CODE_EXAMPLE}`} className="uppercase font-mono tracking-wider" maxLength={LINK_CODE_LENGTH} autoComplete="off" autoCapitalize="characters" spellCheck={false} data-id="element-8848" data-mp="2jbbwef8ghl19" />
              
              <Button type="submit" disabled={isSubmitting} className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto" data-id="element-8849" data-mp="2t0ufxq84hbnq">
                
                {isSubmitting ? 'Bezig…' : 'Koppelen'}
              </Button>
            </form>

            {error && <div className="mt-4 flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3.5 rounded-lg border border-rose-100" data-id="element-8850" data-mp="36v3twiio6g2g">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-rose-500" data-id="element-8851" data-mp="3bv9h9mhwpgeq" />
                <p className="font-medium" data-id="element-8852" data-mp="3gqouqdc05aam">{error}</p>
              </div>}

            {success && <div className="mt-4 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3.5 rounded-lg border border-emerald-100" data-id="element-8853" data-mp="3lsnuzecg3r3c">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-500" data-id="element-8854" data-mp="3pqljsg2b0b67" />
                <p className="font-medium" data-id="element-8855" data-mp="1rfob39118aq9">{success}</p>
              </div>}

            {/*
               Hier stond een bevestigingspaneel dat het kind toonde vóór het
               koppelen. Dat kon niet meer werken: de server koppelt meteen, en
               vóór de koppeling mag de ouder de profielrij van het kind niet
               lezen — er viel dus niets te tonen. Wat overblijft is de
               mededeling ACHTERAF dat er al iemand anders meekijkt.
              */}
            {notice && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mt-4 flex items-start gap-2 text-sm text-amber-900 bg-amber-50 p-3.5 rounded-lg border border-amber-200" data-id="element-8856" data-mp="1a1wmbddqys1k">
              
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600" data-id="element-8857" data-mp="1xrqxzrmbyak8" />
                <p className="font-medium" data-id="element-8858" data-mp="1stz790xf8uhm">{notice}</p>
              </motion.div>}
          </div>
        </SettingsSection>

        {/* Toestemming — apart van het koppelen, want dit is een juridische
             verklaring en geen instelling. */}
        <ConsentPanel data-id="element-8859" data-mp="iwqytc6c4dks" />

        {/*
           Ná het toestemmingspaneel, niet ervoor. De volgorde volgt wat een
           ouder doet: eerst bepalen wát er bewaard mag worden, daarna pas
           inzien of laten wissen wat er al staat.
          */}
        <DataRightsPanel linkedLearners={linkedLearners} data-id="element-8860" data-mp="9vjc7rd6l16j" />

        {/* Logout */}
        <SettingsSection title="Uitloggen" description="Log uit van je ouderaccount" icon={LogOut} accentColor="bg-rose-100 text-rose-600" className="bg-rose-50/30 border-rose-100" data-id="element-8861" data-mp="dw370oaa7spi">
          
          <div className="flex justify-end" data-id="element-8862" data-mp="4xrhyqp2d2z6">
            <Button variant="secondary" className="w-full sm:w-auto text-rose-600 border-rose-200 hover:bg-rose-50 hover:border-rose-300" onClick={nav.handleLogout} data-id="element-8863" data-mp="4yj83lzw68i2">
              
              <LogOut className="w-4 h-4 mr-2" data-id="element-8864" data-mp="3rcki1ys1w35j" />
              Uitloggen
            </Button>
          </div>
        </SettingsSection>
      </div>

      {/*
       Het zwaarste gevolg stond alleen in de melding ACHTERAF: ben je de
       laatste ouder, dan trekt de database de toestemming in en wist hij de
       bewaarde toetsuitslagen (0023/0015). Dat is onomkeerbaar — opnieuw
       koppelen brengt ze niet terug — en het hoort dus vóór de klik te staan,
       niet erna. Een waarschuwing na een onomkeerbare handeling is een
       mededeling, geen keuze.
        De tekst verschilt daarom per situatie. Kijkt er nog iemand anders mee,
       dan gebeurt dat wissen NIET, en dan zou diezelfde waarschuwing juist
       afschrikken voor iets wat niet gebeurt.
       */}
      <ConfirmDialog open={!!unlinkLearnerId} onCancel={() => setUnlinkLearnerId(null)} onConfirm={handleConfirmUnlink} title={unlinkIsLastParent ? 'Ontkoppelen wist de toetsuitslagen' : 'Kind ontkoppelen'} message={unlinkIsLastParent ? `Je bent de enige ouder die meekijkt met ${unlinkLearnerName}. Als je jezelf ontkoppelt, vervalt de toestemming en worden de bewaarde toetsuitslagen definitief verwijderd. Dit kan niet ongedaan worden gemaakt — ook niet door opnieuw te koppelen. ${unlinkLearnerName} kan wel gewoon blijven oefenen.` : `Weet je zeker dat je jezelf wilt ontkoppelen van ${unlinkLearnerName}? Je kunt daarna geen voortgang meer inzien. De andere ouder(s) blijven meekijken, dus er wordt niets verwijderd.`} confirmLabel={unlinkIsLastParent ? 'Ja, ontkoppelen en wissen' : 'Ja, ontkoppelen'} cancelLabel="Annuleren" variant="danger" data-id="element-8865" data-mp="3coss1q4e1pjr" />
      
    </PageLayout>;
}